import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';

import { ModeNamesEnum } from 'app/modules/browse/models/browse-mode';
import { BrowseTypesEnum } from 'app/modules/browse/models/browse-types.enum';
import { Playlist } from '@kinomap-dev/ng-ui-lib';
import { CoachingStore } from 'app/modules/browse/store/coaching/coaching.store';
import { GpsStore } from 'app/modules/browse/store/gps/gps.store';
import { PlaylistStore } from 'app/modules/browse/store/playlist/playlist.store';
import { VideoStore } from 'app/modules/browse/store/video/video.store';
import { WorkoutStore } from 'app/modules/browse/store/workout/workout.store';
import { SearchedContentStore } from '../../store/searched-content/searched-content.store';
import { FavoriteStore } from '../../store/favorite/favorite.store';
import { TranslateModule } from '@ngx-translate/core';
import { PaginationContainerComponent } from '../pagination-container/pagination-container.component';
import { FilterSortBarComponent } from '../filter-sort-bar/filter-sort-bar.component';
import { NgClass, AsyncPipe } from '@angular/common';
import {
  GpsOnly,
  ScenicCardComponent,
  VideoSection,
  Workout,
  Coaching,
  User,
  PlaylistCardComponent,
} from '@kinomap-dev/ng-ui-lib';
import { Store } from '@ngrx/store';
import { selectProfileData } from 'app/store/me/me.selectors';
import { Router } from '@angular/router';
import { browseTypeToPath } from '../../helpers/browseTypeToPath.helpers';
import { FavoriteApi } from '../../api/favorite.api';
import { ResponsiveService } from 'app/services/responsive.service';
import { ModalService } from 'app/services/modal.service';
import { configureConnectionModal } from 'app/helpers/modal-helpers';
import { SwarmLoginService } from 'app/services';

@Component({
  selector: 'app-listing',
  templateUrl: './listing.component.html',
  styleUrls: ['./listing.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    FilterSortBarComponent,
    PaginationContainerComponent,
    AsyncPipe,
    TranslateModule,
    ScenicCardComponent,
    PlaylistCardComponent,
  ],
})
export class ListingComponent {
  private cd: ChangeDetectorRef = inject(ChangeDetectorRef);
  private videoStore: VideoStore = inject(VideoStore);
  private playlistStore: PlaylistStore = inject(PlaylistStore);
  private gpsStore: GpsStore = inject(GpsStore);
  private workoutStore: WorkoutStore = inject(WorkoutStore);
  private coachingStore: CoachingStore = inject(CoachingStore);
  private searchedContentStore: SearchedContentStore =
    inject(SearchedContentStore);
  private favoriteStore: FavoriteStore = inject(FavoriteStore);
  private router: Router = inject(Router);
  private store: Store = inject(Store);
  private favoriteService: FavoriteApi = inject(FavoriteApi);
  private responsiveService: ResponsiveService = inject(ResponsiveService);
  private modalService: ModalService = inject(ModalService);
  private loginService: SwarmLoginService = inject(SwarmLoginService);

  @Input() isSearch: boolean = false;
  @Input() modeName: ModeNamesEnum | null = null;
  @Input() contentType: BrowseTypesEnum | null = null;

  ModeNames: typeof ModeNamesEnum = ModeNamesEnum;
  ContentTypes: typeof BrowseTypesEnum = BrowseTypesEnum;
  items$: Observable<
    (GpsOnly | Workout | Coaching | VideoSection | Playlist)[]
  > = of([]);
  isLoading$: Observable<boolean> = of(false);
  areItemsComplete$: Observable<boolean> = of(false);
  user: User | null = null;
  subscriptions: Subscription[] = [];
  smallScreen$: BehaviorSubject<boolean> = this.responsiveService.smallScreen$;
  xSmallScreen$: BehaviorSubject<boolean> =
    this.responsiveService.xSmallScreen$;

  get hasFilterSortBar(): boolean {
    return (
      this.modeName !== ModeNamesEnum.Library &&
      this.contentType !== BrowseTypesEnum.Playlist
    );
  }

  ngOnInit(): void {
    this.store.select(selectProfileData).subscribe((user: User) => {
      this.user = user;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contentType'] && this.modeName === ModeNamesEnum.Library) {
      this.contentType = changes['contentType'].currentValue;
    }
    this.setContentType();
    this.setItems();
  }

  redirect(id: number, to: 'author' | 'browse'): Promise<boolean> {
    switch (to) {
      case 'author': {
        return this.router.navigate(['/dashboard/profile', id]);
      }
      case 'browse': {
        return this.router.navigate([browseTypeToPath(this.contentType), id]);
      }
    }
  }

  onLikeClick(
    item: VideoSection | Coaching | Workout | GpsOnly | Playlist
  ): void {
    if (!item || !this.contentType) {
      return;
    }

    if (!this.user) {
      this.modalService.reset();
      this.modalService.setClassName('connection-modal');
      configureConnectionModal(this.modalService, this.loginService);
      this.modalService.display();
      item.is_favorite = false;
      return;
    }

    if (item.is_favorite) {
      this.subscriptions.push(
        this.favoriteService
          .deleteFavorite$(item.id, this.contentType)
          .subscribe(() => (item.is_favorite = false))
      );
      return;
    }

    this.subscriptions.push(
      this.favoriteService
        .postFavorite$(item.id, this.contentType)
        .subscribe(() => (item.is_favorite = true))
    );

    item.is_favorite = !item.is_favorite;
  }

  private setItems(): void {
    if (this.isSearch) {
      this.setItemsInSearchMode();
    } else {
      this.setItemsInCatalogMode();
    }
    this.cd.detectChanges();
  }

  private setItemsInCatalogMode(): void {
    switch (this.modeName) {
      case ModeNamesEnum.Videos:
        if (this.contentType === BrowseTypesEnum.Video) {
          this.items$ = this.videoStore.videos$;
          this.isLoading$ = this.videoStore.isLoadingVideos$;
          this.areItemsComplete$ = this.videoStore.areVideosComplete$;
        } else {
          this.items$ = this.playlistStore.playlists$;
          this.isLoading$ = this.playlistStore.isLoadingPlaylists$;
          this.areItemsComplete$ = this.playlistStore.arePlaylistsComplete$;
        }
        break;
      case ModeNamesEnum.Gps:
        this.items$ = this.gpsStore.gpsTracks$;
        this.isLoading$ = this.gpsStore.isLoadingGpsTracks$;
        this.areItemsComplete$ = this.gpsStore.areGpsTracksComplete$;
        break;
      case ModeNamesEnum.Workouts:
        this.items$ = this.workoutStore.workouts$;
        this.isLoading$ = this.workoutStore.isLoadingWorkouts$;
        this.areItemsComplete$ = this.workoutStore.areWorkoutsComplete$;
        break;
      case ModeNamesEnum.Coachings:
        this.items$ = this.coachingStore.coachings$;
        this.isLoading$ = this.coachingStore.isLoadingCoachings$;
        this.areItemsComplete$ = this.coachingStore.areCoachingsComplete$;
        break;
      case ModeNamesEnum.Library:
        switch (this.contentType) {
          case BrowseTypesEnum.Video:
            this.items$ = this.favoriteStore.videos$;
            this.isLoading$ = this.favoriteStore.isLoadingVideos$;
            this.areItemsComplete$ = this.favoriteStore.areVideosComplete$;
            break;
          case BrowseTypesEnum.GpsTrack:
            this.items$ = this.favoriteStore.gpsTracks$;
            this.isLoading$ = this.favoriteStore.isLoadingGpsTracks$;
            this.areItemsComplete$ = this.favoriteStore.areGpsTracksComplete$;
            break;
          case BrowseTypesEnum.Workout:
            this.items$ = this.favoriteStore.workouts$;
            this.isLoading$ = this.favoriteStore.isLoadingWorkouts$;
            this.areItemsComplete$ = this.favoriteStore.areWorkoutsComplete$;
            break;
          case BrowseTypesEnum.Coaching:
            this.items$ = this.favoriteStore.coachings$;
            this.isLoading$ = this.favoriteStore.isLoadingCoachings$;
            this.areItemsComplete$ = this.favoriteStore.areCoachingsComplete$;
            break;
          case BrowseTypesEnum.Playlist:
            this.items$ = this.favoriteStore.playlists$;
            this.isLoading$ = this.favoriteStore.isLoadingPlaylists$;
            this.areItemsComplete$ = this.favoriteStore.arePlaylistsComplete$;
            break;
          default:
            this.items$ = of([]);
            this.isLoading$ = of(false);
            this.areItemsComplete$ = of(true);
        }
        break;
    }
  }

  private setItemsInSearchMode(): void {
    switch (this.contentType) {
      case BrowseTypesEnum.Playlist:
        this.items$ = this.searchedContentStore.searchedPlaylists$;
        this.isLoading$ = this.searchedContentStore.isLoadingSearchedPlaylists$;
        this.areItemsComplete$ =
          this.searchedContentStore.areSearchedPlaylistsComplete$;
        break;
      default:
        this.items$ = this.searchedContentStore.searchedContents$;
        this.isLoading$ = this.searchedContentStore.isLoadingSearchedContents$;
        this.areItemsComplete$ =
          this.searchedContentStore.areSearchedContentsComplete$;
        break;
    }
  }

  private setContentType(): void {
    switch (this.modeName) {
      case ModeNamesEnum.Gps:
        this.contentType = BrowseTypesEnum.GpsTrack;
        return;
      case ModeNamesEnum.Workouts:
        this.contentType = BrowseTypesEnum.Workout;
        return;
      case ModeNamesEnum.Coachings:
        this.contentType = BrowseTypesEnum.Coaching;
        return;
      case ModeNamesEnum.Gps:
        this.contentType = BrowseTypesEnum.GpsTrack;
        return;
    }
  }

  onDisplayMore(): void {
    if (this.isSearch) {
      this.displayMoreInSearchMode();
    } else {
      this.displayMoreInCatalogMode();
    }
  }

  private displayMoreInCatalogMode(): void {
    switch (this.modeName) {
      case ModeNamesEnum.Videos:
        if (this.contentType === BrowseTypesEnum.Video) {
          this.videoStore.getVideos({ nextPage: true });
        } else {
          this.playlistStore.getPlaylists({ nextPage: true });
        }
        return;
      case ModeNamesEnum.Gps:
        this.gpsStore.getGpsTracks({ nextPage: true });
        return;
      case ModeNamesEnum.Workouts:
        this.workoutStore.getWorkouts({ nextPage: true });
        return;
      case ModeNamesEnum.Coachings:
        this.coachingStore.getCoachings({ nextPage: true });
        return;
      case ModeNamesEnum.Library:
        this.favoriteStore.getFavorites({
          nextPage: true,
          contentType: this.contentType,
        });
        return;
    }
  }

  private displayMoreInSearchMode(): void {
    switch (this.contentType) {
      case BrowseTypesEnum.Playlist:
        this.searchedContentStore.getSearchedPlaylists();
        return;
      default:
        this.searchedContentStore.getSearchedContents({ isNextPage: true });
        return;
    }
  }
}
